import { Alert, Button, CircularProgress, useTheme } from "@mui/material";
import CheckRounded from "@mui/icons-material/CheckRounded";
import ErrorOutlineRounded from "@mui/icons-material/ErrorOutlineRounded";
import { isAxiosError } from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AuthPageLayout, AuthStatusBadge } from "../auth/AuthPageLayout";
import { makeGlassButtonSx, primaryButtonSx } from "../auth/authStyles";
import { useUnsubscribeByToken } from "./hooks/useUnsubscribeByToken";

const APP_PATH = "/dashboard";

// What was switched off, in words. A category this build has never heard of
// reads as "these emails".
const CATEGORY_EMAILS = new Map<string, string>([
  ["product_updates", "product update emails"],
  ["following", "alert emails"],
  ["screener", "saved-screen alert emails"],
]);

// The link switches off a whole category. Where a finer control exists, say
// where it lives.
const CATEGORY_NOTES = new Map<string, string>([
  [
    "screener",
    " That covers all your saved screens. To stop alerts for one screen only, turn its alerts off in the Screener instead.",
  ],
]);

/**
 * Where an email's unsubscribe link lands (the API's GET only redirects here).
 * PUBLIC — it must work signed out, so it sits outside the auth gates and is
 * named in the 401-exception list (singleton/axios.ts). Nothing happens until
 * the button is pressed:
 * link scanners fetch URLs, and opening a page must never unsubscribe anyone.
 */
const UnsubscribePage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";
  const unsubscribe = useUnsubscribeByToken();
  const glassButtonSx = makeGlassButtonSx(theme.palette.mode === "dark");

  const isInvalidToken =
    token.length === 0 ||
    (isAxiosError(unsubscribe.error) && unsubscribe.error.response?.status === 400);
  const hasFailed = unsubscribe.isError && !isInvalidToken;

  if (unsubscribe.isSuccess) {
    const emails = CATEGORY_EMAILS.get(unsubscribe.data.category) ?? "these emails";
    const note = CATEGORY_NOTES.get(unsubscribe.data.category) ?? "";
    return (
      <AuthPageLayout
        contentKey="success"
        topSlot={
          <AuthStatusBadge tone="success">
            <CheckRounded sx={{ fontSize: 36 }} />
          </AuthStatusBadge>
        }
        headline="You're unsubscribed"
        supporting={`We won't send you ${emails} any more.${note} You can turn them back on at any time under Notifications in your account settings.`}
      >
        <Button fullWidth onClick={() => navigate(APP_PATH)} sx={glassButtonSx}>
          Back to Insight
        </Button>
      </AuthPageLayout>
    );
  }

  if (isInvalidToken) {
    return (
      <AuthPageLayout
        contentKey="invalid"
        topSlot={
          <AuthStatusBadge tone="error">
            <ErrorOutlineRounded sx={{ fontSize: 34 }} />
          </AuthStatusBadge>
        }
        headline="This link isn't valid"
        supporting="The unsubscribe link is incomplete or no longer works. Try the link in a more recent email, or change your emails under Notifications in your account settings."
      >
        <Button fullWidth onClick={() => navigate(APP_PATH)} sx={glassButtonSx}>
          Back to Insight
        </Button>
      </AuthPageLayout>
    );
  }

  return (
    <AuthPageLayout
      contentKey="confirm"
      headline="Unsubscribe from these emails?"
      supporting="You'll stop getting this kind of email from Insight. Anything you've asked for still shows up in your inbox in the app."
    >
      {hasFailed && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          We couldn't unsubscribe you just now. Please try again.
        </Alert>
      )}
      <Button
        fullWidth
        disabled={unsubscribe.isPending}
        onClick={() => unsubscribe.mutate(token)}
        sx={primaryButtonSx}
      >
        {unsubscribe.isPending && <CircularProgress size={24} color="inherit" />}
        {!unsubscribe.isPending && "Unsubscribe"}
      </Button>
      <Button fullWidth onClick={() => navigate(APP_PATH)} sx={{ ...glassButtonSx, mt: 1.5 }}>
        Keep me subscribed
      </Button>
    </AuthPageLayout>
  );
};

export default UnsubscribePage;
